import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Brain, Code, Database, GraduationCap, Sparkles, Zap } from 'lucide-react';
import SectionHeading from './SectionHeading';

const About: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const skills = [
    { name: 'Java', level: 90, color: 'from-orange-400 to-orange-600' },
    { name: 'Python', level: 95, color: 'from-blue-400 to-blue-600' },
    { name: 'JavaScript', level: 88, color: 'from-yellow-400 to-yellow-600' },
    { name: 'React', level: 85, color: 'from-cyan-400 to-cyan-600' },
    { name: 'Node.js', level: 82, color: 'from-green-400 to-green-600' },
    { name: 'MongoDB', level: 80, color: 'from-green-500 to-green-700' },
  ];

  const stats = [
    { icon: Brain, label: 'AI/ML Projects', value: '10+' },
    { icon: Code, label: 'Lines of Code', value: '50K+' },
    { icon: Database, label: 'Databases', value: '5+' },
    { icon: Zap, label: 'Technologies', value: '15+' },
  ];

  return (
    <section id="about" className="relative py-24">
      <div className="section-shell">
        <div ref={ref}>
          <SectionHeading
            eyebrow="About"
            title={<>Building software with <span className="gradient-text-vibrant">depth, taste, and technical rigor.</span></>}
            description="I care deeply about product quality, intelligent systems, and shipping software that balances engineering precision with a memorable user experience."
          />
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="surface-panel surface-glow">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-cosmic-aurora-sapphire/15 p-3 text-cosmic-aurora-emerald">
                  <Sparkles className="h-5 w-5" />
                </div>
                <p className="text-sm uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Profile</p>
              </div>
              <h3 className="mb-4 text-2xl font-semibold text-slate-900 dark:gradient-text dark:text-transparent">Computer Science Student at DePauw University</h3>
              <p className="mb-4 text-[1.02rem] leading-8 text-slate-700 dark:text-slate-300">
                Hello! I&apos;m Shivansh, a passionate Computer Science student with a strong focus in
                full-stack engineering, AI/ML, and open-source software development.
              </p>
              <p className="mb-4 text-[1.02rem] leading-8 text-slate-700 dark:text-slate-300">
                Currently working as a Software Development Intern at n-Formatix, I specialize in building
                full-stack medical applications with AI-powered features and real-time analytics.
                I&apos;ve also previously worked at Unmute as an AI/ML and Backend Developer, and interned
                at BNutty, streamlining Django workflows and boosting performance.
              </p>
              <p className="text-[1.02rem] leading-8 text-slate-700 dark:text-slate-300">
                Beyond work, I serve as a Teaching Assistant for the DePauw University Computer Science Department.
                With a strong passion for learning and leading, I thrive at the intersection of
                development and community.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="surface-panel spotlight-card p-5 text-left"
                >
                  <stat.icon className="mb-4 h-8 w-8 text-cosmic-aurora-emerald" />
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="surface-panel surface-glow">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-2xl bg-cosmic-aurora-amethyst/15 p-3 text-cosmic-aurora-amethyst">
                  <Code className="h-5 w-5" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:gradient-text dark:text-transparent">Technical Skills</h3>
              </div>
              <div className="mb-6 flex flex-wrap gap-2">
                {skills.map((skill) => (
                  <span key={skill.name} className="badge-pill">
                    {skill.name}
                  </span>
                ))}
              </div>
              <h4 className="text-sm font-medium uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Strength map</h4>
              <div className="mt-5 space-y-4">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700 dark:text-white">{skill.name}</span>
                      <span className="text-sm text-cosmic-aurora-emerald">{skill.level}%</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={isInView ? { width: `${skill.level}%` } : {}}
                        transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                        className={`h-full rounded-full bg-gradient-to-r ${skill.color}`}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="surface-panel"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-2xl bg-cosmic-aurora-emerald/15 p-3 text-cosmic-aurora-gold">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white">Education</h4>
              </div>
              <div className="space-y-3">
                <p className="text-lg font-medium text-slate-900 dark:text-white">DePauw University</p>
                <p className="text-slate-700 dark:text-slate-300">Bachelor&apos;s Degree in Computer Science</p>
                <p className="text-cosmic-aurora-emerald">Expected Graduation: May 2026</p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <span className="badge-pill">Major GPA: 3.50</span>
                  <span className="badge-pill">Cumulative GPA: 3.40</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="surface-panel"
            >
              <div className="mb-4 flex items-center gap-3">
                <Award className="h-6 w-6 text-cosmic-aurora-gold" />
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white">Why teams trust me</h4>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  'Strong product intuition',
                  'Fast learner with systems thinking',
                  'Fluent across backend and frontend',
                  'Comfortable leading and mentoring',
                ].map((item) => (
                  <div key={item} className="badge-pill justify-center py-2 text-center text-sm">
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
